/**
 * V2 (Classic-V2) style-specific section renderers.
 * Extracted from Classic5050StyleV2.jsx for use in GridEngine.
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import Share2 from 'lucide-react/dist/esm/icons/share-2.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import useHeroContent from '../../hooks/useHeroContent'

// Hero: Minimal branding with "Operated by" + partner name + description
export function V2HeroSection({
  mainDraw, partner, t, editMode, handleTranslationUpdate, gameType = 'classic5050', sections,
}) {
  const heroContent = useHeroContent({
    mainDraw,
    partner,
    gameType,
    sections,
    fallbackTitle: gameType === 'modified5050' ? 'Modified 50/50' : '50/50',
    fallbackSubtitle: "By participating in this fundraiser you're directly supporting our community programs and services.",
    fallbackCta: 'Play Now',
  })
  if (!mainDraw) return null
  return (
    <div className="text-center lg:text-left">
      <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--charity-primary)' }}>
        <InlineTranslationEdit
          translationKey={heroContent.eyebrowKey}
          value={heroContent.eyebrowLabel}
          fallback="Operated by"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
        {heroContent.showOperatorName && (
          <span> {heroContent.operatorName}</span>
        )}
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
        <span>{heroContent.organizationName}</span>{' '}
        <InlineTranslationEdit
          translationKey={heroContent.bannerKey}
          value={heroContent.bannerText}
          fallback={heroContent.bannerFallback}
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h1>
      {heroContent.subtitleText && (
        <p className="mt-3 text-gray-600 dark:text-gray-400 max-w-3xl">
          <InlineTranslationEdit
            translationKey={heroContent.subtitleKey}
            value={heroContent.subtitleText}
            fallback={heroContent.subtitleFallback}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            multiline
          />
        </p>
      )}
      <HeroPartnerGlassSelect className="mx-auto lg:mx-0 mt-4 w-full max-w-md" />
      <div className="border-t border-gray-200 dark:border-gray-800 my-4" />
    </div>
  )
}

// Live Draw: Card with countdown, large jackpot, CTA, share button
export function V2LiveDrawSection({
  mainDraw, salesClosed, t, editMode, handleTranslationUpdate,
  purchaseNavigate, timeLeft,
}) {
  if (!mainDraw) return null
  const c = t

  const drawDateLabel = mainDraw.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : ''
  const drawTimeLabel = mainDraw.draw_date
    ? new Date(mainDraw.draw_date).toLocaleTimeString(undefined, {
        hour: 'numeric', minute: '2-digit', hour12: true,
      })
    : ''

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6">
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
        <InlineTranslationEdit
          translationKey="HomeStandardNextDraw"
          value={c?.('HomeStandardNextDraw', 'Next Draw')}
          fallback="Next Draw"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </p>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
        {drawDateLabel || 'Date to be announced'}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        <InlineTranslationEdit
          translationKey="HomeDrawTime"
          value={c?.('HomeDrawTime', 'Draw Time')}
          fallback="Draw Time"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
        : {drawTimeLabel}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-800 pt-4 pb-4">
        {/* V2 Countdown Display */}
        <div className="flex items-center justify-center gap-2">
          {[
            { value: timeLeft?.days || 0, label: 'DAYS' },
            { value: timeLeft?.hours || 0, label: 'HOURS' },
            { value: timeLeft?.minutes || 0, label: 'MINUTES' },
            { value: timeLeft?.seconds || 0, label: 'SECONDS' },
          ].map((unit, i) => (
            <React.Fragment key={unit.label}>
              {i > 0 && <span className="text-gray-400 dark:text-gray-500 font-bold text-lg">:</span>}
              <div className="flex flex-col items-center">
                <div className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 min-w-[48px] text-center">
                  <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{String(unit.value).padStart(2, '0')}</span>
                </div>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">{unit.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
          <InlineTranslationEdit
            translationKey="HomeStandardNextJackpot"
            value={c?.('HomeStandardNextJackpot', 'Next Jackpot')}
            fallback="Next Jackpot"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h3>
        <div className="text-6xl md:text-7xl font-black text-gray-900 dark:text-gray-100 mb-4">
          ${mainDraw.current_jackpot?.toLocaleString() || '0'}
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          <span className="text-2xl font-bold" style={{ color: 'var(--charity-primary)' }}>+</span>
          <InlineTranslationEdit
            translationKey="HomeAdditionalDrawsJackpotText"
            value={c?.('HomeAdditionalDrawsJackpotText', 'Additional prize draws available')}
            fallback="Additional prize draws available"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="ml-2"
          />
        </div>

        <button
          onClick={() => purchaseNavigate?.('/purchase')}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-bold rounded-lg shadow transition-all hover:brightness-110 mb-3"
          style={{ backgroundColor: 'var(--charity-primary)', color: 'var(--charity-primary-contrast)' }}
        >
          <InlineTranslationEdit
            translationKey="HomeStandardCTAButton"
            value={c?.('HomeStandardCTAButton', 'Play Now')}
            fallback="Play Now"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>

        <button
          className="w-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium rounded-lg py-2.5 flex items-center justify-center gap-1"
        >
          <Share2 className="w-4 h-4" />
          <InlineTranslationEdit
            translationKey="HomeStandardShare"
            value={c?.('HomeStandardShare', 'Share with friends')}
            fallback="Share with friends"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>
      </div>
    </div>
  )
}
